/**
 * ============================================================================
 * Enrolments — the digital Student Roster
 * ============================================================================
 * THIS IS THE FALLBACK PATH, NOT THE MAIN ONE (Decision #58). Normally a
 * participant scans the session QR and fills in their own intake block, which
 * populates the roster; the instructor then verifies they were there. This
 * screen is what an instructor or admin opens when someone cannot self-register
 * — the same form, a different door — plus back-office correction.
 *
 * That is also why `coordinator` holds `roster.verify` but not `roster.manage`:
 * coordinators verify rosters, they do not transcribe them. The seed was right;
 * an earlier version of this screen assumed the wrong workflow.
 *
 * The fields are a direct translation of A-B Tech's paper roster form, which is
 * the real specification (Decisions #52–#57). Its columns, in order:
 * Student ID#, Printed Name, Signature, Birthdate, Last Four SS#,
 * Agency Affiliation, Position Title, P = Paid / V = Vol.
 *
 * The signature line is the part that matters. On paper, signing attests to
 * agency affiliation and job classification — and that attestation IS the
 * eligibility evidence for the fee waiver. So the attestation text is shown in
 * full here rather than hidden behind a link: a coordinator ticking that box on
 * someone's behalf should be able to see exactly what is being attested to.
 *
 * Birthdate and Last Four SS# are on the paper form and NOT here (Decision
 * #63, 2026-09-12): the A-B Tech student ID is the only identifier Attest
 * holds. Colleague has the rest. Someone who registered without their ID is an
 * UNCONFIRMED participant — on the roster, earning credit — until the office
 * confirms it in the panel below.
 * ============================================================================
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import Field from '../components/Field.jsx';
import Select from '../components/Select.jsx';

const BLANK = {
  external_id: '',
  full_name: '',
  agency_name: '',
  agency_type: '',
  participant_role_title: '',
  worker_type: '',
  fee_status: 'waived',
  waiver_code: '',
  attestation_agreed: false,
  attestation_method: 'transcribed_paper',
};

export function EnrollmentsPage() {
  const { id } = useParams();

  const [section, setSection] = useState(null);
  const [rows, setRows] = useState(null);
  const [reference, setReference] = useState(null);
  const [form, setForm] = useState(BLANK);

  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  /** roster_id -> the A-B Tech ID being typed for an unconfirmed person. */
  const [confirmIds, setConfirmIds] = useState({});
  const [confirming, setConfirming] = useState(null);

  const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  const load = useCallback(async () => {
    const [detail, list] = await Promise.all([
      apiFetch(`/api/v1/classes/${id}`),
      apiFetch(`/api/v1/classes/${id}/enrollments`),
    ]);
    setSection(detail.section);
    setRows(list ?? []);
  }, [id]);

  useEffect(() => {
    let active = true;
    apiFetch('/api/v1/enrollments/reference')
      .then((data) => active && setReference(data))
      .catch((err) => active && setError(err));
    load().catch((err) => active && setError(err));
    return () => {
      active = false;
    };
  }, [id, load]);

  // Narrowed by worker type, never chosen for them — fire versus rescue is not
  // derivable from anything captured here (Decision #57).
  const waiverOptions = (reference?.waiver_codes ?? []).filter(
    (w) => !form.worker_type || w.workerType === form.worker_type
  );

  /**
   * The office matches an unconfirmed person to their A-B Tech ID (Decision
   * #63). They registered without one, attended, earned credit; this is the
   * link to their college record being made.
   */
  async function confirmIdentity(rosterId) {
    setError(null);
    setNotice(null);
    setConfirming(rosterId);
    try {
      const result = await apiFetch(`/api/v1/enrollments/roster/${rosterId}/confirm-identity`, {
        method: 'PATCH',
        body: JSON.stringify({ external_id: (confirmIds[rosterId] ?? '').trim() }),
      });
      setNotice(`${result.full_name} confirmed as ${result.external_id}.`);
      setConfirmIds((m) => ({ ...m, [rosterId]: '' }));
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setConfirming(null);
    }
  }

  async function addPerson(event) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setSaving(true);

    try {
      await apiFetch(`/api/v1/classes/${id}/enrollments`, {
        method: 'POST',
        body: JSON.stringify({
          person: {
            external_id: form.external_id.trim(),
            full_name: form.full_name.trim(),
          },
          enrollment: {
            agency_name: form.agency_name.trim(),
            agency_type: form.agency_type || null,
            participant_role_title: form.participant_role_title.trim() || null,
            worker_type: form.worker_type || null,
            fee_status: form.fee_status,
            waiver_code: form.waiver_code || null,
            attestation_agreed: form.attestation_agreed,
            attestation_method: form.attestation_method,
          },
        }),
      });

      setNotice(`${form.full_name.trim()} added.`);
      // Keep agency and worker type: a roster is usually entered one crew at a
      // time, and retyping "Buncombe County EMS" ninety-five times is how
      // transcription errors get in.
      setForm((f) => ({
        ...BLANK,
        agency_name: f.agency_name,
        agency_type: f.agency_type,
        worker_type: f.worker_type,
        waiver_code: f.waiver_code,
        fee_status: f.fee_status,
        attestation_agreed: f.attestation_agreed,
        attestation_method: f.attestation_method,
      }));
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  const attestation = reference?.attestation;
  const isWaived = form.fee_status === 'waived';

  return (
    <>
      <p className="crumb">
        <Link to={`/sections/${id}`}>← Back to section</Link>
      </p>

      <h1>Enrolments</h1>
      <p className="muted">
        {section
          ? `${section.official_ccl_title || section.title}${
              section.ccl_course_code ? ` · ${section.ccl_course_code}` : ''
            }`
          : 'Loading…'}
      </p>

      <ErrorBanner error={error} />
      {notice && (
        <p className="banner banner--success" role="status">
          {notice}
        </p>
      )}

      <form className="panel" onSubmit={addPerson} aria-labelledby="add-heading">
        <h2 id="add-heading">Add someone manually</h2>
        <p className="muted">
          Normally people add themselves by scanning the session QR code. Use this when
          somebody can’t — you can hand them the screen to fill in, or enter it from a signed
          paper roster.
        </p>

        <div className="field-row">
          <Field
            label="Student ID"
            value={form.external_id}
            onChange={(v) => set({ external_id: v })}
            hint="Their A-B Tech Colleague ID."
            disabled={saving}
          />
          <Field
            label="Printed name"
            value={form.full_name}
            onChange={(v) => set({ full_name: v })}
            disabled={saving}
          />
        </div>


        <div className="field-row">
          <Field
            label="Agency of record"
            value={form.agency_name}
            onChange={(v) => set({ agency_name: v })}
            hint="The agency they are affiliated with — e.g. Buncombe County EMS, Reems Creek FD."
            disabled={saving}
          />
          <Field
            label="Position title"
            value={form.participant_role_title}
            onChange={(v) => set({ participant_role_title: v })}
            required={false}
            hint="Paramedic, Fire Fighter, Patrol Officer, Dispatcher…"
            disabled={saving}
          />
        </div>

        <div className="field-row">
          <Select
            label="Agency type"
            value={form.agency_type}
            onChange={(v) => set({ agency_type: v })}
            options={(reference?.agency_types ?? []).map((t) => ({
              value: t,
              label: t.charAt(0).toUpperCase() + t.slice(1),
            }))}
            required={false}
            disabled={saving}
          />
          <Select
            label="Paid or volunteer"
            value={form.worker_type}
            onChange={(v) => set({ worker_type: v, waiver_code: '' })}
            options={[
              { value: 'paid', label: 'P — Paid' },
              { value: 'volunteer', label: 'V — Volunteer' },
            ]}
            required={false}
            optional={false}
            hint="The roster form’s P/V column. This narrows the waiver codes below."
            disabled={saving}
          />
        </div>

        <div className="field-row">
          <Select
            label="Fee status"
            value={form.fee_status}
            onChange={(v) => set({ fee_status: v })}
            options={[
              { value: 'waived', label: 'Waived — public-safety affiliation' },
              { value: 'paid', label: 'Paid — invoiced' },
            ]}
            help={
              isWaived
                ? undefined
                : 'Federal law enforcement employees are not eligible for the waiver and may be invoiced.'
            }
            disabled={saving}
          />
          {isWaived && (
            <Select
              label="Waiver code"
              value={form.waiver_code}
              onChange={(v) => set({ waiver_code: v })}
              options={waiverOptions.map((w) => ({ value: w.code, label: w.label }))}
              placeholder={form.worker_type ? 'Choose…' : 'Choose paid or volunteer first…'}
              hint="Fire or rescue — pick the one matching this agency."
              disabled={saving || !form.worker_type}
            />
          )}
        </div>

        {isWaived && attestation && (
          <fieldset className="attestation">
            <legend>Affiliation attestation</legend>
            <blockquote className="attestation__text">“{attestation.text}”</blockquote>
            <p className="muted">{attestation.note}</p>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.attestation_agreed}
                onChange={(e) => set({ attestation_agreed: e.target.checked })}
                disabled={saving}
              />
              <span>This person has attested to the statement above.</span>
            </label>

            <Select
              label="How was it captured?"
              value={form.attestation_method}
              onChange={(v) => set({ attestation_method: v })}
              options={[
                { value: 'transcribed_paper', label: 'Transcribed from a signed paper roster' },
                { value: 'electronic', label: 'Signed electronically by the participant' },
              ]}
              hint="Recorded on the enrolment, because the two are not equally strong evidence."
              disabled={saving}
            />
          </fieldset>
        )}

        <div className="toolbar">
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? 'Adding…' : 'Add to roster'}
          </button>
        </div>
      </form>

      {/* UNCONFIRMED PARTICIPANTS. Registered without an A-B Tech ID — many
          students do not know theirs. They are on the roster and get credit;
          what waits is the link to their college record. This is where the
          office makes it. */}
      {rows && rows.some((r) => r.participants_roster?.identity_status === 'unconfirmed') && (
        <section className="panel" aria-labelledby="unconfirmed-heading">
          <h2 id="unconfirmed-heading">Unconfirmed participants</h2>
          <p className="muted">
            These people registered without their A-B Tech student ID. They are on the roster and
            earn credit as normal; their record cannot reach the college until the ID is confirmed.
            Look them up, enter the ID, confirm.
          </p>
          <ul className="plain-list">
            {rows
              .filter((r) => r.participants_roster?.identity_status === 'unconfirmed')
              .map((r) => {
                const rid = r.participants_roster.id;
                return (
                  <li key={rid} className="schedule__actions" style={{ alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <strong>{r.participants_roster.full_name}</strong>
                      <div className="muted">
                        {r.agency_name}
                        {r.participant_role_title ? ` · ${r.participant_role_title}` : ''}
                      </div>
                    </div>
                    <Field
                      label="A-B Tech student ID"
                      value={confirmIds[rid] ?? ''}
                      onChange={(v) => setConfirmIds((m) => ({ ...m, [rid]: v }))}
                      inputMode="numeric"
                      disabled={confirming === rid}
                    />
                    <button
                      type="button"
                      className="btn btn--primary btn--auto"
                      disabled={confirming === rid || !(confirmIds[rid] ?? '').trim()}
                      onClick={() => confirmIdentity(rid)}
                    >
                      {confirming === rid ? 'Confirming…' : 'Confirm'}
                    </button>
                  </li>
                );
              })}
          </ul>
        </section>
      )}

      <section className="panel" aria-labelledby="roster-heading">
        <h2 id="roster-heading">
          On this roster{rows ? ` (${rows.length})` : ''}
        </h2>

        {rows === null && <p className="muted" aria-busy="true">Loading…</p>}
        {rows !== null && rows.length === 0 && (
          <p className="muted">Nobody yet. Add the first person above.</p>
        )}

        {rows !== null && rows.length > 0 && (
          <div className="schedule__scroll">
            <table className="schedule__table">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Student ID</th>
                  <th scope="col">Name</th>
                  <th scope="col">Agency of record</th>
                  <th scope="col">Position</th>
                  <th scope="col">P/V</th>
                  <th scope="col">Fee</th>
                  <th scope="col">Attested</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id}>
                    <td className="muted">{index + 1}</td>
                    <td>
                      {row.participants_roster?.identity_status === 'unconfirmed' ? (
                        <span className="badge badge--cancelled">unconfirmed</span>
                      ) : (
                        <code>{row.participants_roster?.external_id}</code>
                      )}
                    </td>
                    <td>
                      {row.participants_roster?.full_name}
                      {row.roster_id && (
                        <div>
                          <Link className="muted" to={`/people/${row.roster_id}/affiliations`}>
                            Affiliations
                          </Link>
                        </div>
                      )}
                    </td>
                    <td>{row.agency_name}</td>
                    <td>{row.participant_role_title ?? '—'}</td>
                    <td>{row.worker_type === 'volunteer' ? 'V' : row.worker_type === 'paid' ? 'P' : '—'}</td>
                    <td>
                      {row.fee_status === 'waived' ? (
                        <span className="badge badge--published">{row.waiver_code ?? 'Waived'}</span>
                      ) : (
                        <span className="badge">{row.fee_status === 'pending' ? 'Pending' : 'Invoiced'}</span>
                      )}
                    </td>
                    <td>
                      {row.signature_captured_at ? (
                        <span title={row.eligibility_artifact_id ?? ''}>
                          {row.eligibility_artifact_id?.includes('electronic') ? 'Electronic' : 'Paper'}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="muted">
          The A-B Tech student ID is the only identifier Attest holds. Date of birth and Social
          Security digits are not collected — Colleague has them, and the ID is the link.
        </p>
      </section>
    </>
  );
}

export default EnrollmentsPage;
